import React from "react";

export const inp = {
  fontSize: 12.5,
  border: '1.5px solid #E2E8F0',
  borderRadius: 8,
  padding: '7px 10px',
  outline: 'none',
  width: '100%',
  fontFamily: 'inherit',
  background: '#fff',
  color: '#0F172A',
  transition: 'border-color .15s',
};

export const lbl = {
  fontSize: 11,
  fontWeight: 700,
  color: '#64748B',
  textTransform: 'uppercase',
  letterSpacing: '.5px',
  display: 'block',
  marginBottom: 4,
};

/**
 * Wraps a label + any input child.
 *
 * @param {{ label: string, optional?: boolean, hint?: string, children: React.ReactNode }} props
 */
export const FieldGroup = ({ label, optional, hint, children }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
    <label style={lbl}>
      {label}
      {optional && <span style={{ color: '#94A3B8', fontWeight: 400 }}> (optional)</span>}
    </label>
    {children}
    {hint && (
      <span style={{ fontSize: 10, color: '#94A3B8', marginTop: 1 }}>{hint}</span>
    )}
  </div>
);

export const SectionHeader = ({ icon, title, sub }) => (
  <div style={{ paddingBottom: 12, borderBottom: '1px solid #E2E8F0', marginBottom: 4 }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <span style={{ fontSize: 18 }}>{icon}</span>
      <span style={{ fontSize: 14, fontWeight: 800, color: '#0F172A' }}>{title}</span>
    </div>
    {sub && (
      <div style={{ fontSize: 11, color: '#94A3B8', marginTop: 3, marginLeft: 26 }}>{sub}</div>
    )}
  </div>
);

export const SectionDivider = ({ label }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '4px 0' }}>
    <div style={{ flex: 1, height: 1, background: '#E2E8F0' }} />
    <span style={{ fontSize: 10, color: '#94A3B8', fontWeight: 700, letterSpacing: '0.08em' }}>
      {label.toUpperCase()}
    </span>
    <div style={{ flex: 1, height: 1, background: '#E2E8F0' }} />
  </div>
);

export const Toggle = ({ checked, onChange, disabled }) => (
  <button
    type="button"
    disabled={disabled}
    onClick={() => !disabled && onChange(!checked)}
    style={{
      width: 38,
      height: 22,
      borderRadius: 11,
      border: 'none',
      cursor: disabled ? 'not-allowed' : 'pointer',
      background: checked ? '#1D4ED8' : '#CBD5E1',
      position: 'relative',
      transition: 'background 0.2s',
      flexShrink: 0,
      opacity: disabled ? 0.55 : 1,
    }}
  >
    <div
      style={{
        position: 'absolute',
        top: 3,
        left: checked ? 19 : 3,
        width: 16,
        height: 16,
        borderRadius: '50%',
        background: '#fff',
        transition: 'left 0.2s',
        boxShadow: '0 1px 3px rgba(0,0,0,0.25)',
      }}
    />
  </button>
);

export const ToggleRow = ({ label, sub, checked, onChange, disabled }) => (
  <div
    style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      background: '#F8FAFC',
      border: '1px solid #E2E8F0',
      borderRadius: 9,
      padding: '10px 14px',
    }}
  >
    <div>
      <div style={{ fontSize: 13, fontWeight: 600, color: '#0F172A' }}>{label}</div>
      {sub && <div style={{ fontSize: 11, color: '#94A3B8', marginTop: 2 }}>{sub}</div>}
    </div>
    <Toggle checked={checked} onChange={onChange} disabled={disabled} />
  </div>
);

export const InfoBox = ({ children, color = '#1D4ED8' }) => (
  <div
    style={{
      background: `${color}0d`,
      border: `1px solid ${color}33`,
      borderRadius: 9,
      padding: '10px 14px',
      fontSize: 12,
      color: '#334155',
      lineHeight: 1.65,
    }}
  >
    {children}
  </div>
);

export const Row2 = ({ children, gap = 12 }) => (
  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap }}>{children}</div>
);

export const Row3 = ({ children, gap = 12 }) => (
  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap }}>{children}</div>
);

export const SliderField = ({ label, value, min, max, step = 1, onChange, hint }) => (
  <FieldGroup label={label} hint={hint}>
    <input
      type="range"
      min={min}
      max={max}
      step={step}
      value={value}
      onChange={(e) => onChange(step < 1 ? parseFloat(e.target.value) : parseInt(e.target.value))}
      style={{ width: '100%', accentColor: '#1D4ED8' }}
    />
  </FieldGroup>
);

export const TagListInput = ({ values = [], onChange, placeholder = 'Type and press Enter…' }) => {
  const [draft, setDraft] = React.useState('');

  const add = () => {
    const v = draft.trim();
    if (!v || values.includes(v)) return;
    onChange([...values, v]);
    setDraft('');
  };

  return (
    <div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: 6, minHeight: 24 }}>
        {values.map((v, i) => (
          <span
            key={i}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 4,
              background: '#EFF6FF', border: '1px solid #BFDBFE',
              borderRadius: 20, padding: '2px 9px',
              fontSize: 11, color: '#1D4ED8', fontWeight: 600,
            }}
          >
            {v}
            <button
              type="button"
              onClick={() => onChange(values.filter((_, j) => j !== i))}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#EF4444', fontSize: 13, padding: 0, lineHeight: 1 }}
            >×</button>
          </span>
        ))}
        {values.length === 0 && (
          <span style={{ fontSize: 11, color: '#CBD5E1', fontStyle: 'italic' }}>None yet</span>
        )}
      </div>
      <div style={{ display: 'flex', gap: 6 }}>
        <input
          style={{ ...inp, flex: 1 }}
          value={draft}
          placeholder={placeholder}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
        />
        <button
          type="button"
          onClick={add}
          style={{ padding: '5px 12px', borderRadius: 7, border: '1.5px solid #BFDBFE', background: '#EFF6FF', color: '#1D4ED8', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}
        >Add</button>
      </div>
    </div>
  );
};